import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Grid,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Paper,
  Tooltip,
  TextField,
  InputAdornment,
  IconButton,
  Stack,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,

  // A-I additions
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  Tabs,
  Tab,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useParams, useNavigate } from 'react-router-dom';
import { alpha, lighten } from '@mui/material/styles';
import { BASEURL, PRIMARYCOLOR } from '../../Constants';
import Nav from './nav';

// icons
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// A-I icons
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BugReportIcon from '@mui/icons-material/BugReport';
import TerminalIcon from '@mui/icons-material/Terminal';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import ShieldIcon from '@mui/icons-material/Shield';
import RadarIcon from '@mui/icons-material/Radar';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';

/* ---------- helpers ---------- */
const VERIFY_THRESHOLD = 25;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const scoreColor = (s) => (s >= 75 ? '#d32f2f' : s >= 50 ? '#f57c00' : '#1976d2');
const scoreLabel = (s) => (s >= 75 ? 'Likely' : s >= 50 ? 'Possible' : 'Low');
const willVerify = (s) => num(s) >= VERIFY_THRESHOLD;

const trunc = (str, len = 40) => (str?.length > len ? `${str.slice(0, len - 3)}…` : str);

// ✅ schema-correct: findings live under attack.exploit_results.findings
const getFindings = (atk) => (Array.isArray(atk?.exploit_results?.findings) ? atk.exploit_results.findings : []);

const sevKey = (sev) => {
  const s = String(sev || '').toLowerCase();
  if (s.includes('critical')) return 'critical';
  if (s.includes('high')) return 'high';
  if (s.includes('medium')) return 'medium';
  if (s.includes('low')) return 'low';
  if (s.includes('info')) return 'info';
  return 'info';
};

const sevColor = (key) =>
  ({
    critical: '#b71c1c',
    high: '#d32f2f',
    medium: '#f57c00',
    low: '#1976d2',
    info: '#455a64',
  }[key] || '#455a64');

const attackIcon = (name = '') => {
  const n = name.toLowerCase();
  if (n.includes('sql')) return <BugReportIcon fontSize="small" />;
  if (n.includes('auth') || n.includes('jwt') || n.includes('session')) return <ShieldIcon fontSize="small" />;
  if (n.includes('ssrf') || n.includes('request forgery')) return <RadarIcon fontSize="small" />;
  if (n.includes('rce') || n.includes('command') || n.includes('os command')) return <PriorityHighIcon fontSize="small" />;
  return <WarningAmberIcon fontSize="small" />;
};

/* ---------- markdown → MUI ---------- */
const mdComponents = {
  h1: (props) => <Typography variant="h5" sx={{ mt: 2, mb: 1, fontWeight: 900 }} {...props} />,
  h2: (props) => <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 900 }} {...props} />,
  p: (props) => <Typography variant="body2" sx={{ mb: 1.2, lineHeight: 1.7 }} {...props} />,
  li: (props) => (
    <li style={{ marginLeft: 16 }}>
      <Typography component="span" variant="body2" {...props} />
    </li>
  ),
  em: (props) => (
    <Typography component="em" variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }} {...props} />
  ),
  code: ({ inline, ...props }) =>
    inline ? (
      <code
        style={{
          background: '#f5f5f5',
          border: '1px solid #e0e0e0',
          borderRadius: 6,
          padding: '1px 6px',
          fontFamily: 'monospace',
          fontSize: '0.85em',
        }}
        {...props}
      />
    ) : (
      <Box
        component="code"
        sx={{
          display: 'block',
          bgcolor: '#f5f5f5',
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          px: 1.5,
          py: 1,
          fontSize: '0.85rem',
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          overflowX: 'auto',
        }}
        {...props}
      />
    ),
};

const mdComponentsTerminal = {
  h1: (props) => <Typography variant="h5" sx={{ mt: 2, mb: 1, fontWeight: 900, color: 'inherit' }} {...props} />,
  h2: (props) => <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 900, color: 'inherit' }} {...props} />,
  p: (props) => <Typography variant="body2" sx={{ mb: 1.2, lineHeight: 1.7, color: 'inherit' }} {...props} />,
  li: (props) => (
    <li style={{ marginLeft: 16 }}>
      <Typography component="span" variant="body2" sx={{ color: 'inherit' }} {...props} />
    </li>
  ),
  em: (props) => (
    <Typography component="em" variant="body2" sx={{ fontStyle: 'italic', color: alpha('#e8f0ff', 0.75) }} {...props} />
  ),
  code: ({ inline, ...props }) =>
    inline ? (
      <code
        style={{
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 6,
          padding: '1px 6px',
          fontFamily: 'monospace',
          fontSize: '0.85em',
          color: '#e8f0ff',
        }}
        {...props}
      />
    ) : (
      <Box
        component="code"
        sx={{
          display: 'block',
          bgcolor: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 2,
          px: 1.5,
          py: 1,
          fontSize: '0.85rem',
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          overflowX: 'auto',
          color: '#e8f0ff',
        }}
        {...props}
      />
    ),
};

/* ---------- main ---------- */
const PentestExternalThreatModel = () => {
  const { pentest_id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [endpoints, setEndpoints] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);

  // right panel filters
  const [search, setSearch] = useState('');
  const [kbdIndex, setKbdIndex] = useState(0);

  // A-I: attack workbench state
  const [attackSearch, setAttackSearch] = useState('');
  const [minScore, setMinScore] = useState(0);
  const [sortMode, setSortMode] = useState('SCORE'); // SCORE | NAME
  const [assetSort, setAssetSort] = useState('RISK'); // RISK | NAME
  const [tab, setTab] = useState(0); // 0 attacks, 1 Threat Model
  const [rawOpen, setRawOpen] = useState(false);
  const [selectedAttack, setSelectedAttack] = useState(null);
  const [showLowConfidence, setShowLowConfidence] = useState(false);

  /* fetch once --------------------------------------------------- */
  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) return navigate('/');

    fetch(`${BASEURL}/pentests/external/threat_model?pentest_id=${pentest_id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (!data.success) throw new Error(data.message || 'Load error');

        const apiModel = {
          id: 0,
          path: 'External Overview',
          attacks: [],
          md: data.threat_model.api || '',
        };

        const epModels = (data.threat_model.endpoints || []).map((ep, i) => ({
          id: i + 1,
          path: ep.path || `Endpoint ${i + 1}`,
          attacks: ep.threat_model?.attacks || [],
          md: ep.threat_model?.threat_model || '',
        }));

        setEndpoints([apiModel, ...epModels]);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        navigate('/');
      });
  }, [navigate, pentest_id]);

  // reset UI when switching endpoints
  useEffect(() => {
    setTab(0);
    setAttackSearch('');
    setMinScore(0);
    setSortMode('SCORE');
    setSelectedAttack(null);
    setRawOpen(false);
    setShowLowConfidence(false);
  }, [activeIdx]);

  const active = useMemo(() => endpoints[activeIdx] || null, [endpoints, activeIdx]);

  /* ---------- endpoint meta & overview stats ------------------- */
  const endpointMeta = useMemo(() => {
    const map = {};
    endpoints.slice(1).forEach((ep) => {
      const attacks = ep.attacks || [];
      const scores = attacks.map((a) => num(a.score));
      const maxScore = scores.length ? Math.max(...scores) : 0;
      const likelyCount = attacks.filter((a) => num(a.score) >= 75).length;
      const queuedCount = attacks.filter((a) => willVerify(a.score)).length;

      const ports = new Set();
      const services = new Set();
      attacks.forEach((a) => {
        (a.ports || []).forEach((p) => ports.add(String(p)));
        (a.service || []).forEach((s) => services.add(String(s)));
      });

      // ✅ schema-correct findings: attack.exploit_results.findings
      const sev = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
      let findingsCount = 0;
      attacks.forEach((a) => {
        const f = getFindings(a);
        findingsCount += f.length;
        f.forEach((x) => {
          sev[sevKey(x.severity)] += 1;
        });
      });

      map[ep.path] = {
        maxScore,
        likelyCount,
        queuedCount,
        totalAttacks: attacks.length,
        portsCount: ports.size,
        servicesCount: services.size,
        findingsCount,
        sev,
      };
    });

    return map;
  }, [endpoints]);

  const overviewStats = useMemo(() => {
    const eps = endpoints.slice(1);

    let totalAttacks = 0;
    const uniqueAttacks = new Set();
    const ports = new Set();
    const services = new Set();

    const scoreBins = { low: 0, mid: 0, high: 0, vhigh: 0 }; // 0-24, 25-49, 50-74, 75+
    const sev = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    let totalFindings = 0;

    eps.forEach((ep) => {
      (ep.attacks || []).forEach((a) => {
        totalAttacks += 1;
        uniqueAttacks.add(a.name);

        (a.ports || []).forEach((p) => ports.add(String(p)));
        (a.service || []).forEach((s) => services.add(String(s)));

        const s = num(a.score);
        if (s >= 75) scoreBins.vhigh += 1;
        else if (s >= 50) scoreBins.high += 1;
        else if (s >= 25) scoreBins.mid += 1;
        else scoreBins.low += 1;

        // ✅ schema-correct findings
        const f = getFindings(a);
        totalFindings += f.length;
        f.forEach((x) => {
          sev[sevKey(x.severity)] += 1;
        });
      });
    });

    const endpointsWithFindings = eps.reduce((acc, ep) => {
      const m = endpointMeta[ep.path];
      return acc + (m?.findingsCount > 0 ? 1 : 0);
    }, 0);

    return {
      totalEndpoints: eps.length,
      totalAttacks,
      uniqueAttacks: uniqueAttacks.size,
      ports: ports.size,
      services: services.size,
      scoreBins,
      totalFindings,
      endpointsWithFindings,
      sev,
    };
  }, [endpoints, endpointMeta]);

  /* --- filters for endpoint list ------------------------------- */
  const filteredEndpoints = useMemo(() => {
    const q = search.trim().toLowerCase();
    const overview = endpoints.find((ep) => ep.id === 0);

    const rankedAssets = endpoints
      .filter((ep) => ep.id !== 0)
      .filter((ep) => {
        if (!q) return true;
        return ep.path.toLowerCase().includes(q);
      })
      .sort((left, right) => {
        if (assetSort === 'NAME') {
          return left.path.localeCompare(right.path);
        }

        const leftMeta = endpointMeta[left.path] || {};
        const rightMeta = endpointMeta[right.path] || {};

        const leftPriority = (leftMeta.findingsCount > 0 ? 4 : 0) + (leftMeta.likelyCount > 0 ? 2 : 0) + (leftMeta.maxScore >= 50 ? 1 : 0);
        const rightPriority = (rightMeta.findingsCount > 0 ? 4 : 0) + (rightMeta.likelyCount > 0 ? 2 : 0) + (rightMeta.maxScore >= 50 ? 1 : 0);

        return (
          rightPriority - leftPriority ||
          num(rightMeta.maxScore) - num(leftMeta.maxScore) ||
          num(rightMeta.findingsCount) - num(leftMeta.findingsCount) ||
          left.path.localeCompare(right.path)
        );
      });

    return overview ? [overview, ...rankedAssets] : rankedAssets;
  }, [assetSort, endpointMeta, endpoints, search]);

  // deep-link from overview to endpoint detail
  const goToEndpointPath = (path) => {
    const idx = endpoints.findIndex((e) => e.path === path);
    if (idx > -1) setActiveIdx(idx);
  };

  // keyboard navigation for the right list (↑/↓/Enter)
  const onKeyDownList = (e) => {
    if (!filteredEndpoints.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setKbdIndex((p) => Math.min(p + 1, filteredEndpoints.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setKbdIndex((p) => Math.max(p - 1, 0));
    } else if (e.key === 'Enter') {
      const selected = filteredEndpoints[kbdIndex];
      if (!selected) return;
      const idx = endpoints.findIndex((e) => e.id === selected.id);
      if (idx > -1) setActiveIdx(idx);
    }
  };

  /* --- A-I: attack workbench list ------------------------------ */
  const attacksForUI = useMemo(() => {
    if (activeIdx === 0) return [];
    const list = (active?.attacks || [])
      .filter((a) => num(a?.score) >= minScore)
      .filter((a) => {
        const q = attackSearch.trim().toLowerCase();
        if (!q) return true;
        return (a.name || '').toLowerCase().includes(q) || (a.reason || '').toLowerCase().includes(q);
      });

    list.sort((a, b) => {
      if (sortMode === 'NAME') return (a.name || '').localeCompare(b.name || '');
      return num(b.score) - num(a.score);
    });

    return list;
  }, [active, activeIdx, minScore, attackSearch, sortMode]);

  const groupedAttacks = useMemo(() => {
    const groups = {
      likely: [],
      possible: [],
      low: [],
    };

    attacksForUI.forEach((attack) => {
      const score = num(attack?.score);
      if (score >= 75) groups.likely.push(attack);
      else if (score >= 25) groups.possible.push(attack);
      else groups.low.push(attack);
    });

    return groups;
  }, [attacksForUI]);

  const assetOverviewRows = useMemo(() => {
    return endpoints
      .slice(1)
      .map((ep) => {
        const meta = endpointMeta[ep.path] || {};
        return {
          id: ep.id,
          path: ep.path,
          maxScore: num(meta.maxScore),
          likelyCount: num(meta.likelyCount),
          queuedCount: num(meta.queuedCount),
          totalAttacks: num(meta.totalAttacks),
          findingsCount: num(meta.findingsCount),
          portsCount: num(meta.portsCount),
          servicesCount: num(meta.servicesCount),
        };
      })
      .sort(
        (left, right) =>
          (right.findingsCount > 0) - (left.findingsCount > 0) ||
          right.maxScore - left.maxScore ||
          right.likelyCount - left.likelyCount ||
          left.path.localeCompare(right.path)
      );
  }, [endpointMeta, endpoints]);

  const overviewLaunchRows = useMemo(
    () => assetOverviewRows.filter((row) => row.findingsCount > 0 || row.likelyCount > 0 || row.maxScore >= VERIFY_THRESHOLD).slice(0, 10),
    [assetOverviewRows]
  );

  const assetsWithLikelyPaths = useMemo(
    () => assetOverviewRows.reduce((count, row) => count + (row.likelyCount > 0 ? 1 : 0), 0),
    [assetOverviewRows]
  );

  const attackSections = useMemo(() => {
    const sections = [];

    if (groupedAttacks.likely.length) {
      sections.push({
        key: 'likely',
        title: 'Likely attack paths',
        helper: 'Highest-confidence hypotheses that deserve immediate analyst review.',
        rows: groupedAttacks.likely,
        color: '#d32f2f',
      });
    }

    if (groupedAttacks.possible.length) {
      sections.push({
        key: 'possible',
        title: 'Possible attack paths',
        helper: 'Plausible hypotheses above the verification threshold that should stay in active review.',
        rows: groupedAttacks.possible,
        color: '#ef6c00',
      });
    }

    if (groupedAttacks.low.length && showLowConfidence) {
      sections.push({
        key: 'low',
        title: 'Low-confidence attack paths',
        helper: 'Lower-confidence hypotheses that are still useful for analyst awareness.',
        rows: groupedAttacks.low,
        color: '#1976d2',
      });
    }

    return sections;
  }, [groupedAttacks, showLowConfidence]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: '#fff',
      }}
    >
      <Nav />

      <Box sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden' }}>
        {/* MAIN SCROLL AREA -------------------------------------- */}
        <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, overflowY: 'auto' }}>
          {/* Header */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 950, lineHeight: 1.15 }}>
                {activeIdx === 0 ? 'External Asset Threat Overview' : 'Asset Threat Review'}
              </Typography>
              {activeIdx !== 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Hypotheses, findings, and model narrative for asset: <b>{active?.path}</b>
                </Typography>
              )}
            </Box>

            <Stack direction="row" spacing={1} sx={{ mt: { xs: 1, md: 0 } }}>
              <Button variant="contained" size="small" startIcon={<InfoOutlinedIcon />} onClick={() => setActiveIdx(0)}>
                Overview
              </Button>
            </Stack>
          </Stack>
          {/* Threat Model Disclaimer Banner */}
          {activeIdx === 0 && (
            <Alert
              icon={<AutoAwesomeIcon />}
              severity="info"
              sx={{
                mb: 2,
                borderRadius: 3,
                bgcolor: alpha(PRIMARYCOLOR, 0.06),
                border: `1px solid ${alpha(PRIMARYCOLOR, 0.18)}`,
                '& .MuiAlert-message': { width: '100%' },
              }}
            >
              <Typography sx={{ fontWeight: 950 }}>Threat paths are hypotheses, not confirmed vulnerabilities.</Typography>
              <Typography variant="body2" color="text.secondary">
                Scores of {VERIFY_THRESHOLD}+ are prioritized for validation, but a high score alone does not mean the asset is vulnerable.
              </Typography>
            </Alert>
          )}

          {/* OVERVIEW DASHBOARD */}
          {activeIdx === 0 ? (
            <>
              {/* Top KPIs */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      TOTAL ASSETS
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 950 }}>
                      {overviewStats.totalEndpoints}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Select an asset on the right to drill in
                    </Typography>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      ASSETS WITH LIKELY PATHS
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 950 }}>
                      {assetsWithLikelyPaths}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Assets with at least one high-confidence path
                    </Typography>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      ASSETS WITH FINDINGS
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 950 }}>
                      {overviewStats.endpointsWithFindings}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Assets with confirmed findings already recorded
                    </Typography>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      UNIQUE ATTACK TYPES
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 950 }}>
                      {overviewStats.uniqueAttacks}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Distinct attack families across the modeled scope
                    </Typography>
                  </Card>
                </Grid>
              </Grid>

              <Card
                sx={{
                  bgcolor: '#fff',
                  mb: 2,
                  borderRadius: 3,
                  boxShadow: 2,
                  border: `1px solid ${alpha(PRIMARYCOLOR, 0.12)}`,
                }}
              >
                <CardContent>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.5}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    sx={{ mb: 1.5 }}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 950 }}>
                        Assets needing attention
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Start here, then jump directly into the assets with likely paths or confirmed findings.
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={`${overviewLaunchRows.length || assetOverviewRows.slice(0, 10).length} assets shown`}
                      sx={{ fontWeight: 900, bgcolor: alpha(PRIMARYCOLOR, 0.08) }}
                    />
                  </Stack>

                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
                    <Chip label={`Unique ports: ${overviewStats.ports}`} sx={{ fontWeight: 900 }} />
                    <Chip label={`Unique services: ${overviewStats.services}`} sx={{ fontWeight: 900 }} />
                    <Chip
                      label={`Confirmed findings: ${overviewStats.totalFindings}`}
                      sx={{
                        fontWeight: 900,
                        bgcolor: alpha(overviewStats.totalFindings > 0 ? '#d32f2f' : PRIMARYCOLOR, 0.08),
                        color: overviewStats.totalFindings > 0 ? '#d32f2f' : PRIMARYCOLOR,
                      }}
                    />
                  </Stack>

                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <b>Asset</b>
                          </TableCell>
                          <TableCell align="center">
                            <b>Score</b>
                          </TableCell>
                          <TableCell align="center">
                            <b>Likely</b>
                          </TableCell>
                          <TableCell align="center">
                            <b>Findings</b>
                          </TableCell>
                          <TableCell align="center">
                            <b>Attack Paths</b>
                          </TableCell>
                          <TableCell>
                            <b>Surface</b>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(overviewLaunchRows.length ? overviewLaunchRows : assetOverviewRows.slice(0, 10)).map((row) => (
                          <TableRow key={row.path} hover sx={{ cursor: 'pointer' }} onClick={() => goToEndpointPath(row.path)}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 900, wordBreak: 'break-all' }}>
                                {row.path}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                size="small"
                                label={`${Math.round(row.maxScore)}`}
                                sx={{
                                  fontWeight: 900,
                                  bgcolor: alpha(scoreColor(row.maxScore), 0.1),
                                  color: scoreColor(row.maxScore),
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">{row.likelyCount}</TableCell>
                            <TableCell align="center">
                              <Chip
                                size="small"
                                label={row.findingsCount}
                                sx={{
                                  fontWeight: 900,
                                  bgcolor: alpha(row.findingsCount > 0 ? '#d32f2f' : PRIMARYCOLOR, 0.08),
                                  color: row.findingsCount > 0 ? '#d32f2f' : PRIMARYCOLOR,
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">{row.totalAttacks}</TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {row.portsCount} ports - {row.servicesCount} services
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              <Alert
                severity="info"
                icon={<ChevronRightIcon />}
                sx={{
                  mb: 4,
                  borderRadius: 3,
                  bgcolor: alpha(PRIMARYCOLOR, 0.05),
                  border: `1px dashed ${alpha(PRIMARYCOLOR, 0.22)}`,
                }}
              >
                <Typography sx={{ fontWeight: 950 }}>Next step: open an asset</Typography>
                <Typography variant="body2" color="text.secondary">
                  Click a row above or choose an asset on the right to go straight into its ranked paths, model narrative, and confirmed findings.
                </Typography>
              </Alert>
            </>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      ASSET
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 900, mt: 0.5, wordBreak: 'break-all' }}>
                      {active?.path}
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      MAX SCORE
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 950, color: scoreColor(endpointMeta[active?.path]?.maxScore || 0) }}>
                      {Math.round(endpointMeta[active?.path]?.maxScore || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Highest attack-path likelihood on this asset
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      ATTACK PATHS
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 950 }}>
                      {endpointMeta[active?.path]?.totalAttacks || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {endpointMeta[active?.path]?.likelyCount || 0} likely and {endpointMeta[active?.path]?.queuedCount || 0} queued for verification
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      CONFIRMED FINDINGS
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 950,
                        color: (endpointMeta[active?.path]?.findingsCount || 0) > 0 ? '#d32f2f' : 'inherit',
                      }}
                    >
                      {endpointMeta[active?.path]?.findingsCount || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {endpointMeta[active?.path]?.portsCount || 0} ports and {endpointMeta[active?.path]?.servicesCount || 0} services informed this model
                    </Typography>
                  </Card>
                </Grid>
              </Grid>

              {/* ASSET VIEW — ATTACK REVIEW */}
              <Card
                sx={{
                  mb: 3,
                  borderRadius: 3,
                  boxShadow: 2,
                  border: `1px solid ${alpha(PRIMARYCOLOR, 0.12)}`,
                  overflow: 'hidden',
                }}
              >
                <CardContent sx={{ pb: 1 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Stack spacing={0.25}>
                      <Typography variant="h6" sx={{ fontWeight: 950 }}>
                        Asset attack review
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Review ranked attack paths for <b>{active?.path}</b> and open any path for evidence and findings.
                      </Typography>

                      {(() => {
                        const m = endpointMeta[active?.path] || {};
                        return (
                          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                            <Chip size="small" label={`Attacks: ${m.totalAttacks || 0}`} />
                            <Chip size="small" label={`Likely: ${m.likelyCount || 0}`} sx={{ bgcolor: '#fff3e0' }} />
                            <Chip
                              size="small"
                              label={`Findings: ${m.findingsCount || 0}`}
                              sx={{
                                bgcolor: alpha('#d32f2f', 0.08),
                                color: (m.findingsCount || 0) > 0 ? '#d32f2f' : 'inherit',
                                fontWeight: 900,
                              }}
                            />
                          </Stack>
                        );
                      })()}
                    </Stack>

                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<TerminalIcon />}
                      onClick={() => setRawOpen(true)}
                      sx={{ borderStyle: 'dashed' }}
                    >
                      Fullscreen narrative
                    </Button>
                  </Stack>

                  <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    sx={{
                      minHeight: 38,
                      '& .MuiTab-root': { minHeight: 38, textTransform: 'none', fontWeight: 900 },
                    }}
                  >
                    <Tab icon={<BugReportIcon />} iconPosition="start" label={`Attack Paths (${attacksForUI.length})`} />
                    <Tab icon={<TerminalIcon />} iconPosition="start" label="Model Narrative" />
                  </Tabs>
                </CardContent>

                <Divider />

                {/* TAB 0: ATTACKS */}
                {tab === 0 && (
                  <CardContent>
                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          size="small"
                          fullWidth
                          value={attackSearch}
                          onChange={(e) => setAttackSearch(e.target.value)}
                          placeholder="Search attack paths (name / evidence)..."
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <Stack spacing={0.5}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
                            Minimum likelihood score: {minScore}
                          </Typography>
                          <Slider size="small" value={minScore} onChange={(_, v) => setMinScore(v)} min={0} max={100} />
                        </Stack>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <ToggleButtonGroup
                          size="small"
                          value={sortMode}
                          exclusive
                          onChange={(_, v) => v && setSortMode(v)}
                          fullWidth
                        >
                          <ToggleButton value="SCORE" sx={{ fontWeight: 900 }}>
                            Sort: Score
                          </ToggleButton>
                          <ToggleButton value="NAME" sx={{ fontWeight: 900 }}>
                            Sort: Name
                          </ToggleButton>
                          </ToggleButtonGroup>
                      </Grid>
                    </Grid>

                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                      <Chip
                        size="small"
                        label={`Likely: ${groupedAttacks.likely.length}`}
                        sx={{ fontWeight: 900, bgcolor: alpha('#d32f2f', 0.08), color: '#d32f2f' }}
                      />
                      <Chip
                        size="small"
                        label={`Possible: ${groupedAttacks.possible.length}`}
                        sx={{ fontWeight: 900, bgcolor: alpha('#ef6c00', 0.08), color: '#ef6c00' }}
                      />
                      <Chip
                        size="small"
                        label={`Low confidence: ${groupedAttacks.low.length}`}
                        sx={{ fontWeight: 900, bgcolor: alpha(PRIMARYCOLOR, 0.08), color: PRIMARYCOLOR }}
                      />
                    </Stack>

                    <Stack spacing={2}>
                      {attackSections.map((section) => (
                        <Box key={section.key}>
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            justifyContent="space-between"
                            spacing={1}
                            sx={{ mb: 1 }}
                          >
                            <Stack spacing={0.25}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
                                {section.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {section.helper}
                              </Typography>
                            </Stack>
                            <Chip
                              size="small"
                              label={`${section.rows.length} path${section.rows.length === 1 ? '' : 's'}`}
                              sx={{
                                fontWeight: 900,
                                bgcolor: alpha(section.color, 0.08),
                                color: section.color,
                                border: `1px solid ${alpha(section.color, 0.16)}`,
                              }}
                            />
                          </Stack>

                          <List dense disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {section.rows.map((atk, idx) => {
                              const score = num(atk.score);
                              const findings = getFindings(atk);
                              const findingCount = findings.length;
                              const confirmed = atk?.exploit_results?.vulnerable === true || findingCount > 0;
                              const portsLabel = Array.isArray(atk?.ports) && atk.ports.length ? atk.ports.join(', ') : 'No ports mapped';
                              const servicesLabel =
                                Array.isArray(atk?.service) && atk.service.length ? atk.service.join(', ') : 'Service not mapped';

                              return (
                                <Paper
                                  key={`${section.key}-${idx}-${atk.name}`}
                                  variant="outlined"
                                  sx={{
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    borderColor: alpha(section.color, 0.18),
                                    bgcolor: '#fff',
                                  }}
                                >
                                  <ListItemButton onClick={() => setSelectedAttack(atk)} sx={{ alignItems: 'flex-start', py: 1.25 }}>
                                    <Box sx={{ mt: 0.25, mr: 1, color: alpha('#000', 0.65) }}>{attackIcon(atk.name)}</Box>

                                    <Box sx={{ flex: 1 }}>
                                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
                                          {atk.name}
                                        </Typography>

                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
                                          <Chip
                                            size="small"
                                            label={`${scoreLabel(score)} - ${score}`}
                                            sx={{
                                              fontWeight: 950,
                                              bgcolor: alpha(scoreColor(score), 0.12),
                                              color: scoreColor(score),
                                              border: `1px solid ${alpha(scoreColor(score), 0.24)}`,
                                            }}
                                          />
                                          <Chip
                                            size="small"
                                            variant={willVerify(score) ? 'filled' : 'outlined'}
                                            label={willVerify(score) ? 'Ready for verification' : 'Analyst watchlist'}
                                            sx={{
                                              fontWeight: 900,
                                              borderStyle: willVerify(score) ? 'solid' : 'dashed',
                                              bgcolor: willVerify(score) ? alpha('#ef6c00', 0.08) : 'transparent',
                                              color: willVerify(score) ? '#ef6c00' : PRIMARYCOLOR,
                                            }}
                                          />
                                          <Chip
                                            size="small"
                                            label={`Findings: ${findingCount}`}
                                            sx={{
                                              fontWeight: 900,
                                              bgcolor: alpha(confirmed ? '#d32f2f' : PRIMARYCOLOR, 0.08),
                                              color: confirmed ? '#d32f2f' : PRIMARYCOLOR,
                                              border: `1px solid ${alpha(confirmed ? '#d32f2f' : PRIMARYCOLOR, 0.14)}`,
                                            }}
                                          />
                                        </Stack>
                                      </Stack>

                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                          mt: 0.5,
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                        }}
                                      >
                                        {atk.reason}
                                      </Typography>

                                      <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1 }}>
                                        <Chip size="small" variant="outlined" label={`Ports: ${portsLabel}`} />
                                        <Chip size="small" variant="outlined" label={`Services: ${servicesLabel}`} />
                                      </Stack>

                                      <Box sx={{ mt: 1 }}>
                                        <Box sx={{ position: 'relative' }}>
                                          <LinearProgress
                                            variant="determinate"
                                            value={Math.max(0, Math.min(100, score))}
                                            sx={{ height: 8, borderRadius: 99, bgcolor: alpha(PRIMARYCOLOR, 0.08) }}
                                          />
                                          <Box
                                            sx={{
                                              position: 'absolute',
                                              top: -2,
                                              left: `${VERIFY_THRESHOLD}%`,
                                              transform: 'translateX(-1px)',
                                              width: 2,
                                              height: 12,
                                              bgcolor: alpha('#000', 0.35),
                                            }}
                                          />
                                        </Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                          Likelihood score with verification threshold marker
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </ListItemButton>
                                </Paper>
                              );
                            })}
                          </List>
                        </Box>
                      ))}

                      {groupedAttacks.low.length > 0 && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setShowLowConfidence((prev) => !prev)}
                          sx={{ alignSelf: 'flex-start', borderStyle: 'dashed', fontWeight: 900 }}
                        >
                          {showLowConfidence
                            ? 'Hide low-confidence attack paths'
                            : `Show low-confidence attack paths (${groupedAttacks.low.length})`}
                        </Button>
                      )}

                      {attacksForUI.length === 0 && (
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderStyle: 'dashed', bgcolor: '#fff' }}>
                          <Typography sx={{ fontWeight: 950 }}>No attack paths match your filters</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Try lowering the minimum score or clearing search.
                          </Typography>
                        </Paper>
                      )}
                    </Stack>
                  </CardContent>
                )}

                {/* TAB 1: Threat Model */}
                {tab === 1 && (
                  <CardContent>
                    <Paper
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        p: 2,
                        bgcolor: alpha(PRIMARYCOLOR, 0.04),
                        borderColor: alpha(PRIMARYCOLOR, 0.16),
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography sx={{ fontWeight: 950 }}>Model Narrative</Typography>
                        <Button size="small" startIcon={<OpenInFullIcon />} onClick={() => setRawOpen(true)}>
                          Fullscreen narrative
                        </Button>
                      </Stack>

                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                        {active?.md || '_No threat model available._'}
                      </ReactMarkdown>
                    </Paper>
                  </CardContent>
                )}
              </Card>

              {/* Small “what this is” card */}
              <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 1, border: `1px solid ${alpha(PRIMARYCOLOR, 0.1)}` }}>
                <CardContent>
                  <Typography sx={{ fontWeight: 950, mb: 0.5 }}>How to use this view</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select an asset on the right, review the ranked attack paths, then open a path to see why it applies and whether any findings were confirmed.
                  </Typography>
                </CardContent>
              </Card>
            </>
          )}
        </Box>

        {/* ENDPOINT SIDE PANEL --------------------------------- */}
        <Paper
          elevation={3}
          sx={{
            position: 'sticky',
            top: 64,
            height: 'calc(100vh - 64px)',
            flex: '0 0 360px',
            maxWidth: 360,
            minWidth: 360,
            borderLeft: `1px solid ${alpha(PRIMARYCOLOR, 0.12)}`,
            bgcolor: '#ffffff',
            overflowY: 'auto',
          }}
        >
          {/* action + search */}
          <Box sx={{ p: 1.5, borderBottom: '1px solid #eee', position: 'sticky', top: 0, bgcolor: '#fff', zIndex: 1 }}>
            <Alert
              severity="info"
              icon={<ChevronRightIcon />}
              sx={{
                mb: 1.25,
                borderRadius: 3,
                bgcolor: alpha(PRIMARYCOLOR, 0.05),
                border: `1px dashed ${alpha(PRIMARYCOLOR, 0.22)}`,
              }}
            >
              <Typography sx={{ fontWeight: 950 }}>
                {activeIdx === 0 ? 'Select an asset to review attack paths' : 'Select another asset'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Each asset shows its top score, likely paths, findings count, and model narrative.
              </Typography>
            </Alert>

            <ToggleButtonGroup
              size="small"
              value={assetSort}
              exclusive
              onChange={(_, value) => value && setAssetSort(value)}
              fullWidth
              sx={{ mb: 1.25 }}
            >
              <ToggleButton value="RISK" sx={{ fontWeight: 900 }}>
                Highest risk
              </ToggleButton>
              <ToggleButton value="NAME" sx={{ fontWeight: 900 }}>
                Name
              </ToggleButton>
            </ToggleButtonGroup>

            <TextField
              size="small"
              fullWidth
              placeholder="Search assets..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setKbdIndex(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <List
            dense
            onKeyDown={onKeyDownList}
            subheader={
              <ListSubheader component="div" sx={{ bgcolor: '#fff', fontWeight: 950, fontSize: '1rem' }}>
                Assets
              </ListSubheader>
            }
          >
            {filteredEndpoints.map((ep) => {
              const isSelected = endpoints[activeIdx]?.id === ep.id;

              if (ep.id === 0) {
                return (
                  <Tooltip key={ep.id} title="Overview dashboard" placement="left" arrow enterDelay={300}>
                    <ListItemButton
                      selected={isSelected}
                      onClick={() => setActiveIdx(0)}
                      sx={{
                        alignItems: 'flex-start',
                        py: 1.25,
                        borderLeft: isSelected ? `3px solid ${PRIMARYCOLOR}` : '3px solid transparent',
                        '&.Mui-selected': { bgcolor: lighten(PRIMARYCOLOR, 0.9) },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Chip size="small" label="OVERVIEW" variant="outlined" />
                            <Typography variant="body2" sx={{ fontWeight: isSelected ? 950 : 600 }}>
                              External Threat Overview
                            </Typography>
                          </Stack>
                        }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                            Portfolio view and quick picks
                        </Typography>
                      }
                      sx={{ m: 0 }}
                      />
                    </ListItemButton>
                  </Tooltip>
                );
              }

              const m = endpointMeta[ep.path] || {};
              const likely = m.likelyCount || 0;
              const findings = m.findingsCount || 0;

              return (
                <Tooltip key={ep.id} title={ep.path} placement="left" arrow enterDelay={300}>
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => setActiveIdx(endpoints.findIndex((e) => e.id === ep.id))}
                    sx={{
                      alignItems: 'flex-start',
                      py: 1.25,
                      borderLeft: isSelected ? `3px solid ${PRIMARYCOLOR}` : '3px solid transparent',
                      '&.Mui-selected': { bgcolor: lighten(PRIMARYCOLOR, 0.9) },
                      '&:hover': { bgcolor: alpha(PRIMARYCOLOR, 0.04) },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Stack spacing={0.5}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                            <Typography variant="body2" sx={{ fontWeight: isSelected ? 950 : 700, wordBreak: 'break-all' }}>
                              {trunc(ep.path, 52)}
                            </Typography>
                            <Chip
                              size="small"
                              label={Math.round(m.maxScore || 0)}
                              sx={{
                                fontWeight: 950,
                                bgcolor: alpha(scoreColor(m.maxScore || 0), 0.1),
                                color: scoreColor(m.maxScore || 0),
                              }}
                            />
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {m.totalAttacks || 0} paths modeled, {likely} likely, {findings} findings
                          </Typography>
                        </Stack>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.75, flexWrap: 'wrap', gap: 0.75 }}>
                          <Chip size="small" label={`Likely: ${likely}`} sx={{ bgcolor: '#fff3e0', color: '#fb8c00' }} />
                          <Chip
                            size="small"
                            label={`Findings: ${findings}`}
                            sx={{
                              bgcolor: alpha('#d32f2f', 0.08),
                              color: findings > 0 ? '#d32f2f' : 'inherit',
                              fontWeight: 900,
                            }}
                          />
                          <Chip
                            variant="outlined"
                            size="small"
                            label="Open review"
                            icon={<ChevronRightIcon />}
                            sx={{ borderStyle: 'dashed' }}
                          />
                        </Box>
                      }
                      sx={{ m: 0 }}
                    />
                  </ListItemButton>
                </Tooltip>
              );
            })}
          </List>
        </Paper>
      </Box>

      {/* H) Attack Intel Drawer */}
      <Drawer
        anchor="right"
        open={!!selectedAttack}
        onClose={() => setSelectedAttack(null)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 480 },
            p: 2,
            borderLeft: `1px solid ${alpha(PRIMARYCOLOR, 0.12)}`,
          },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 950 }}>
            Attack analysis
          </Typography>
          <IconButton onClick={() => setSelectedAttack(null)}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {selectedAttack &&
          (() => {
            const score = num(selectedAttack.score);

            // ✅ schema-correct findings
            const findings = getFindings(selectedAttack);
            const findingCount = findings.length;
            const confirmed = selectedAttack?.exploit_results?.vulnerable === true || findingCount > 0;

            const aSev = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
            findings.forEach((f) => {
              aSev[sevKey(f.severity)] += 1;
            });

            return (
              <>
                <Stack spacing={0.75} sx={{ mb: 2 }}>
                  <Typography sx={{ fontWeight: 950 }}>{selectedAttack.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Review why this path applies to <b>{active?.path}</b> and whether validation produced confirmed findings.
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip size="small" label={`Asset: ${trunc(active?.path || 'Unknown asset', 36)}`} sx={{ fontWeight: 900 }} />
                    <Chip
                      size="small"
                      label={willVerify(score) ? 'Ready for verification' : 'Below verification threshold'}
                      sx={{
                        fontWeight: 900,
                        bgcolor: alpha(willVerify(score) ? '#ef6c00' : PRIMARYCOLOR, 0.08),
                        color: willVerify(score) ? '#ef6c00' : PRIMARYCOLOR,
                      }}
                    />
                  </Stack>
                </Stack>

                <Grid container spacing={1.25} sx={{ mb: 2 }}>
                  <Grid item xs={4}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3, borderColor: alpha(scoreColor(score), 0.18) }}>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>
                        SCORE
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 950, color: scoreColor(score) }}>
                        {score}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {scoreLabel(score)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3, borderColor: alpha('#ef6c00', 0.18) }}>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>
                        STATUS
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 950, mt: 0.5 }}>
                        {willVerify(score) ? 'Queued for validation' : 'Analyst review'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Threshold: {VERIFY_THRESHOLD}+
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3, borderColor: alpha('#d32f2f', 0.18) }}>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>
                        FINDINGS
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 950, color: findingCount > 0 ? '#d32f2f' : 'inherit' }}>
                        {findingCount}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {confirmed ? 'Confirmed evidence recorded' : 'No confirmed evidence yet'}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: alpha(PRIMARYCOLOR, 0.16) }}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 950 }}>
                    Why this path applies
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {selectedAttack.reason || 'No reason provided.'}
                  </Typography>
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    mt: 2,
                    borderColor: alpha(PRIMARYCOLOR, 0.16),
                  }}
                >
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 950 }}>
                    Observed surface
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1 }}>
                    {(Array.isArray(selectedAttack?.ports) && selectedAttack.ports.length ? selectedAttack.ports : ['No ports mapped']).map((port, index) => (
                      <Chip key={`port-${port}-${index}`} size="small" label={`Port ${port}`} variant="outlined" />
                    ))}
                    {(Array.isArray(selectedAttack?.service) && selectedAttack.service.length ? selectedAttack.service : ['Service not mapped']).map((service, index) => (
                      <Chip key={`service-${service}-${index}`} size="small" label={service} variant="outlined" />
                    ))}
                  </Stack>
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    mt: 2,
                    borderStyle: 'dashed',
                    borderColor: alpha(PRIMARYCOLOR, 0.18),
                    bgcolor: alpha(PRIMARYCOLOR, 0.03),
                  }}
                >
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 950 }}>
                    Confirmed findings
                  </Typography>

                  {findingCount === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No findings recorded for this attack yet.
                    </Typography>
                  ) : (
                    <List dense disablePadding sx={{ mt: 1 }}>
                      {findings.map((f, i) => {
                        const name = f?.name || f?.title || f?.finding || `Finding ${i + 1}`;
                        const sk = sevKey(f?.severity);
                        const sev = (f?.severity || 'info').toString();

                        return (
                          <ListItemText
                            key={`${name}-${i}`}
                            primary={
                              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                <Typography variant="body2" sx={{ fontWeight: 900, pr: 1 }}>
                                  {name}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={sev.toUpperCase()}
                                  sx={{
                                    fontWeight: 950,
                                    bgcolor: alpha(sevColor(sk), 0.1),
                                    color: sevColor(sk),
                                    border: `1px solid ${alpha(sevColor(sk), 0.18)}`,
                                  }}
                                />
                              </Stack>
                            }
                            sx={{ py: 0.75, borderBottom: `1px solid ${alpha('#000', 0.06)}` }}
                          />
                        );
                      })}
                    </List>
                  )}

                  <Divider sx={{ my: 1.25 }} />

                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 950 }}>
                    Findings breakdown
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                    {(['critical', 'high', 'medium', 'low', 'info']).map((k) => (
                      <Chip
                        key={k}
                        size="small"
                        label={`${k.toUpperCase()} · ${aSev[k]}`}
                        sx={{
                          fontWeight: 950,
                          bgcolor: alpha(sevColor(k), 0.1),
                          color: sevColor(k),
                          border: `1px solid ${alpha(sevColor(k), 0.18)}`,
                        }}
                      />
                    ))}
                  </Stack>

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
                    Context: <b>{active?.path}</b>
                  </Typography>
                </Paper>
              </>
            );
          })()}
      </Drawer>

      {/* I) Fullscreen Threat Model Dialog */}
      <Dialog open={rawOpen} onClose={() => setRawOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ fontWeight: 950 }}>Model Narrative</DialogTitle>
        <DialogContent dividers>
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              borderRadius: 3,
              bgcolor: alpha(PRIMARYCOLOR, 0.06),
              border: `1px solid ${alpha(PRIMARYCOLOR, 0.18)}`,
            }}
          >
            This is the agent’s Threat Model output. It may include speculative hypotheses and should not be treated as verified vulnerabilities.
          </Alert>

          <Paper
            variant="outlined"
            sx={{
              borderRadius: 3,
              p: 2,
              bgcolor: '#0b1220',
              color: '#e8f0ff',
              borderColor: alpha(PRIMARYCOLOR, 0.25),
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponentsTerminal}>
              {active?.md || '_No threat model available._'}
            </ReactMarkdown>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRawOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PentestExternalThreatModel;
